import React from "react";
import type { CheckBoxesRef } from "~/screens/App/components/VirtualTable";

type useListSelectionKeyboardShortcutsProps<T> = {
  data: Array<T>;
  inverse?: boolean;
};

type rangeSelectionCheckboxes = {
  baseCheckboxIndex: number | undefined;
  lastCheckboxIndex: number | undefined;
};

const useListSelectionKeyboardShortcuts = <T extends object>(
  props: useListSelectionKeyboardShortcutsProps<T>
) => {
  const [checkboxesRef, setCheckboxesRef] = React.useState<CheckBoxesRef>();

  const [shiftPressed, _setShiftPressed] = React.useState(false);
  const shiftPressedRef = React.useRef(shiftPressed);
  const setShiftPressed = (data: boolean) => {
    shiftPressedRef.current = data;
    _setShiftPressed(data);
  };

  const [isSpreadSelection, setIsSpreadSelection] =
    React.useState<boolean>(false);
  const [spreadCheckboxes, setSpreadCheckboxes] =
    React.useState<rangeSelectionCheckboxes>({
      baseCheckboxIndex: undefined,
      lastCheckboxIndex: undefined,
    });

  React.useEffect(() => {
    if (
      spreadCheckboxes["baseCheckboxIndex"] !== undefined &&
      spreadCheckboxes["lastCheckboxIndex"] !== undefined &&
      spreadCheckboxes["baseCheckboxIndex"] !==
        spreadCheckboxes["lastCheckboxIndex"]
    ) {
      setIsSpreadSelection(true);
    }
  }, [spreadCheckboxes]);

  React.useEffect(() => {
    const refs = checkboxesRef ? Object.values(checkboxesRef) : [];
    if (isSpreadSelection) {
      const index_elements = [
        spreadCheckboxes?.baseCheckboxIndex,
        spreadCheckboxes?.lastCheckboxIndex,
      ].sort((a, b) => {
        if (a !== undefined && b !== undefined) {
          return a - b;
        } else {
          return 0;
        }
      });
      for (
        let i = index_elements[0] ?? 0;
        i <= (index_elements[1] ?? 1) - 1;
        i++
      ) {
        if (checkboxesRef) {
          if (!refs[i]?.current?.checked) {
            refs[i].current?.click();
          }

          setIsSpreadSelection(false);
        }
      }
    }
  }, [isSpreadSelection]);

  // This useEffect defines the setCheckBoxesRef each time the data is updated
  React.useEffect(() => {
    if (props.data?.length) {
      let counter = 1;
      const arrayOfRef = [];

      for (let counter = 1; counter <= props.data.length; counter++) {
        arrayOfRef.push(React.createRef<null | undefined>());
      }

      setCheckboxesRef(
        arrayOfRef.reduce((accumulate, current) => {
          accumulate[`checkbox${counter}`] = current;
          counter++;
          return accumulate;
        }, {} as CheckBoxesRef)
      );
    }

    return () => {
      setCheckboxesRef(undefined);
    };
  }, [props.data]);

  //this useEffect is to handle the keyboard shortcuts
  React.useEffect(() => {
    let currentFocus = 1;
    const checkboxes = checkboxesRef ? Object.values(checkboxesRef) : [];
    const numberOfCheckboxes = checkboxes.length;

    if (checkboxesRef) {
      setSpreadCheckboxes({
        baseCheckboxIndex: props.inverse ? numberOfCheckboxes : 0,
        lastCheckboxIndex: undefined,
      });
    }

    const keyUpEventHandler = (event: KeyboardEvent) => {
      if (event.key === "Shift") {
        setShiftPressed(false);
      }
    };

    const keyDownEventHandler = (event: KeyboardEvent) => {
      if (event.shiftKey) {
        setShiftPressed(true);
      }

      // Clean all selected checkboxes
      if (event.key === "Escape") {
        for (let i = 0; i < numberOfCheckboxes; i++) {
          if (checkboxes[i]?.current?.checked) {
            checkboxes[i].current?.click();
          }
        }

        setSpreadCheckboxes({
          baseCheckboxIndex: props.inverse ? numberOfCheckboxes : 0,
          lastCheckboxIndex: undefined,
        });
      }
      //Check all the fields and if are all checked, it unchecked all the fields
      if (event.metaKey && (event.key === "a" || event.key === "A")) {
        if (checkboxesRef) {
          setSpreadCheckboxes({
            baseCheckboxIndex: props.inverse ? numberOfCheckboxes : 0,
            lastCheckboxIndex: undefined,
          });
        }

        const allSelected =
          checkboxes
            .map((checkbox) => {
              return checkbox.current?.checked;
            })
            .filter((checkboxStatus) => checkboxStatus).length ===
          checkboxes.length;

        if (checkboxesRef && numberOfCheckboxes) {
          if (allSelected) {
            Object.values(checkboxesRef).forEach((checkbox) => {
              checkbox ? checkbox?.current?.click() : null;
            });
          } else {
            Object.values(checkboxesRef).forEach((checkbox) => {
              !checkbox.current?.checked ? checkbox?.current?.click() : null;
            });
          }
        }
      }

      //Control the focus on the checkboxes
      if (event.ctrlKey && event.key === "ArrowUp" && !props.inverse) {
        if (checkboxesRef && numberOfCheckboxes) {
          currentFocus > 1 ? currentFocus-- : null;
          checkboxesRef[`checkbox${currentFocus}`].current?.focus();
        }
      }

      //Control the focus on the checkboxes
      if (event.ctrlKey && event.key === "ArrowDown" && !props.inverse) {
        if (checkboxesRef && numberOfCheckboxes) {
          currentFocus < numberOfCheckboxes ? currentFocus++ : null;
          checkboxesRef[`checkbox${currentFocus}`].current?.focus();
        }
      }

      //Control the focus on the checkboxes inverse
      if (event.ctrlKey && event.key === "ArrowDown" && props.inverse) {
        if (checkboxesRef && numberOfCheckboxes) {
          currentFocus > 1 ? currentFocus-- : null;
          checkboxesRef[`checkbox${currentFocus}`].current?.focus();
        }
      }

      //Control the focus on the checkboxes inverse
      if (event.ctrlKey && event.key === "ArrowUp" && props.inverse) {
        if (checkboxesRef && numberOfCheckboxes) {
          currentFocus < numberOfCheckboxes ? currentFocus++ : null;
          checkboxesRef[`checkbox${currentFocus}`].current?.focus();
        }
      }

      //Select a focused checkbox
      if (event.ctrlKey && event.key === "Enter") {
        if (checkboxesRef) {
          checkboxes?.forEach((checkbox) => {
            if (document.activeElement === checkbox?.current) {
              checkbox?.current?.click();
            }
          });
        }
      }
    };

    const onChangeCheckboxEventHandler = (event: Event) => {
      if (
        shiftPressedRef.current &&
        (event.target as HTMLInputElement).checked
      ) {
        if (checkboxesRef) {
          const index = Object.values(checkboxesRef)
            .map((checkboxRef) => checkboxRef.current)
            .indexOf(event.target as HTMLInputElement);
          setSpreadCheckboxes((oldValue) => {
            const newValue = { ...oldValue };
            newValue.lastCheckboxIndex = index;
            return newValue;
          });
        }
      } else if (!shiftPressedRef.current && checkboxesRef) {
        setSpreadCheckboxes({
          baseCheckboxIndex: Object.values(checkboxesRef)
            .map((checkboxRef) => checkboxRef.current)
            .indexOf(event.target as HTMLInputElement),
          lastCheckboxIndex: undefined,
        });
      }
    };

    if (
      checkboxesRef &&
      Object.values(checkboxesRef).length === props.data.length
    ) {
      // this timeout is added to wait for the checkboxes ref are subscribed to the <input type="checkbox"> element
      setTimeout(() => {
        Object.values(checkboxesRef).forEach((checkbox) => {
          checkbox.current?.addEventListener(
            "change",
            onChangeCheckboxEventHandler
          );
        });
      }, 400);
    }

    window.addEventListener("keydown", keyDownEventHandler);
    window.addEventListener("keyup", keyUpEventHandler);

    /* When the component is unmounted, remove the listeners */
    return () => {
      window.removeEventListener("keydown", keyDownEventHandler);
      window.removeEventListener("keyup", keyUpEventHandler);

      if (checkboxesRef) {
        Object.values(checkboxesRef).forEach((checkbox) => {
          checkbox.current?.removeEventListener(
            "change",
            onChangeCheckboxEventHandler
          );
        });
      }
    };
  }, [checkboxesRef, props.inverse]);

  return { checkboxesRef, shiftPressed };
};

export type { useListSelectionKeyboardShortcutsProps };
export { useListSelectionKeyboardShortcuts };
